import { useRef, useState, type ReactNode } from 'react';
import { DealStageBadge } from '@/components/StageSwatch';
import { DealLostReasonDialog } from '@/components/deals/DealLostReasonDialog';
import { Notice } from '@/components/ui/Notice';
/*
 * The Next task notes, shared with `DealFormModal` rather than rewritten here.
 * What would drift between two copies is the BODY that lands on a company's
 * timeline and the rule deciding whether a note is written at all — and neither
 * screen can see the other disagreeing with it, because both report success.
 */
import {
  describeNextTaskNoteFailures,
  nextTaskNoteText,
  writeNextTaskNotes,
} from '@/components/deals/next-task-note';
import {
  InlineField,
  type InlineFieldOption,
  type InlineSaveResult,
} from '@/components/ui/InlineField';
import { ApiError, api } from '@/lib/api';
import { dealValue, dealValueIsFromLines } from '@/lib/deals';
import {
  DEAL_MAX_LENGTHS,
  validateDealAmount,
  validateDealClosingDate,
  validateDealCurrency,
  validateDealName,
} from '@/lib/document-validation';
import { formatDateOnly, formatDecimalString } from '@/lib/format';
import { denialReason } from '@/lib/permissions';
import { selectablePickerOptions, useDealPickers } from '@/lib/use-deal-pickers';
import { useAuth } from '@/lib/use-auth';
/*
 * ⚠️ `use-user-options` is deliberately NOT imported. It fed only the Owner
 * picker, replaced by the read-only Account lead row (see below) — the lead
 * arrives already resolved, so there is nothing here to look up.
 */
import type { Deal, DealUpdateInput, LookupSummary, StageSummary } from '@/lib/types';

/**
 * A deal's header fields, edited where they are read. Same shape as the
 * company record's inline editors — `InlineField` owns the interaction, this
 * file owns the API. Renders a bare `<div>` of `<dl>` groups, no panel chrome.
 *
 * ## Correctness rules
 *
 * - **One key per PATCH** — `{"amount":"18000.00"}`, never the record.
 * - **An untouched field sends nothing.** `InlineField` short-circuits a
 *   commit equal to the stored value, so every `value` below must be the RAW
 *   stored string, never formatted, or the comparison is against a rendering.
 * - **Clearing sends `null`, never `''`** — except `dealName`, which is not
 *   nullable (`dealName: null` is a 422), so an emptied name is refused
 *   client-side instead.
 * - **A failed save keeps what was typed** — `saveDealField` REPORTS rather
 *   than throws, so the field can render the outcome beside its draft.
 * - ⚠️ **One field carries a SECOND request: Next task.** Its text is also a
 *   note on the deal and on that deal's company, so its save has two phases
 *   with no transaction across them and nothing to roll back. The report is
 *   `partial` — the column saved, the note did not — and never `error`, which
 *   would send somebody to retype a value that is already stored.
 *
 * ## Deliberately NOT editable here
 *
 * - **`projectId`** — removed from the deal form entirely; not a `FormValues`
 *   key there, so re-adding it here would risk emitting `projectId: null` and
 *   silently clearing a project (and its derived `cities`).
 * - **`companyId` / `contactId`** — need the debounced lookup search that
 *   lives inside `DealFormModal`; re-parenting also moves the derived
 *   `accountLead`. Read-only here.
 * - **`xeroQuoteId`** — a hand-typed third copy from the Zoho era; only
 *   `f_xero_links` is authoritative. End state is with the backend.
 *   ⚠️ It is no longer editable or READABLE on any deal surface: the owner
 *   took the box off `DealFormModal` and the read-only row off
 *   `DealDetailPage` on 2026-08-31, and it left `DealCreateInput` with them,
 *   so nothing in this app can write it. `Deal.xeroQuoteId` still reads back.
 * - **`description`** — removed on the same decision, from here and from
 *   `DealFormModal`. ⚠️ **The column holds real data on live deals** and is
 *   still returned; only the controls and the write went. See `TextKey`.
 * - ⚠️ **`ownerId`** — no longer written from here, matching `DealFormModal`.
 *   **This is NOT a rename of Owner**: `owner` and `accountLead` are
 *   different people, so a deal shows a different name in the same slot
 *   before and after this change. `deal.owner` is still stored and read, just
 *   not writable from this app. The guard is structural — `ownerId` is not a
 *   member of `IdKey` — because `ownerId: null` reaching the body CLEARS the
 *   column with a 200 and nothing to restore from.
 * - **`accountLeadId`** — edited on `DealDetailPage`, not here.
 *   ⚠️ Unlike a quote's or an invoice's, a deal's account lead is genuinely
 *   WRITABLE (`d_deals.account_lead_id`, a real OVERRIDE column). Not
 *   duplicated here because `null` does not mean "nobody" — it CLEARS THE
 *   OVERRIDE and returns the deal to following its company, so the empty
 *   option must NAME the fallback (`companyAccountLead`), which is only in
 *   scope on the page.
 * - ⚠️ **`probability`** — the per-deal OVERRIDE was retired on the owner's
 *   instruction, 2026-08-31: a deal's percentage is ALWAYS its stage's, so
 *   there is no longer anything here to type. It is shown, not edited — the
 *   stage row's `DealStageBadge` renders the stage's own percentage, which is
 *   the single number expected revenue uses.
 *   ⚠️ Unlike the derived columns below, the server has NOT stopped accepting
 *   it: `PATCH /deals` still declares `probability` `nullish()`, so nothing
 *   would refuse a body carrying one. The guard is structural and is the only
 *   guard there is — `probability` is not a member of `ValueKey`, so
 *   `dealPatch` cannot build a body naming it.
 * - **`cities`, `project`, `lineTotal`, the audit columns** — derived or
 *   generated; sending one is a 422.
 */

/*
 * ⚠️ The maximums and field-rule validators are IMPORTED from
 * `@/lib/document-validation` — this file must not grow its own copy. They
 * live there rather than being exported from `DealFormModal` because
 * oxlint's `react(only-export-components)` fires on a component module
 * exporting plain functions.
 *
 * Nothing here trims: validators get the RAW draft, because `InlineField`
 * compares that draft against the stored value to decide whether anything
 * changed at all.
 */

/**
 * The plain nullable text columns.
 *
 * ⚠️ `description` is ABSENT, and its absence IS the guard — the same shape as
 * `ownerId` on `IdKey` and `probability` on `ValueKey` below. The owner took
 * the Description control off every deal surface on 2026-08-31, and a key left
 * in this union with no control would reach `dealPatch` and emit
 * `description: null`, which CLEARS the column: a live deal's description
 * destroyed by somebody editing its amount, with a 200 and nothing to restore
 * from. `PATCH /deals` still ACCEPTS the key, so this union is the only thing
 * stopping a body carrying one — `tsc` refuses a `dealPatch` case for a member
 * that is not here.
 *
 * ⚠️ **The column and `Deal.description` are UNTOUCHED and hold real data.**
 * This hides the field; it deletes nothing. Restoring the control means this
 * union, the `dealPatch` case, the `rawValue` case and the `textField` mount.
 */
type TextKey = 'nextStep';
/**
 * The foreign-key columns this file offers a picker for.
 *
 * ⚠️ `ownerId` is absent, and its absence IS the guard — see the module
 * comment. `accountLeadId` isn't here either: real on `DealUpdateInput`, but
 * edited in `DealFormModal`, where the empty option can name the fallback.
 */
type IdKey = 'stageId' | 'dealTypeId' | 'leadSourceId';
/**
 * Everything a text control writes here.
 *
 * ⚠️ `probability` is ABSENT, and its absence is the guard — the same shape as
 * `ownerId` on `IdKey` above. The owner retired the per-deal override on
 * 2026-08-31: a deal's percentage is always its STAGE's, so there is nothing to
 * type. `PATCH /deals` still ACCEPTS `probability` (`deals.schema.ts` declares
 * it `nullish()`), so the server refuses nothing here — this union is the only
 * thing stopping a body carrying one, because `dealPatch` builds over it and
 * `tsc` refuses a case for a member that is not in it.
 */
type ValueKey = TextKey | 'dealName' | 'amount' | 'currency' | 'closingDate';

/**
 * The one-key patch for one column. An exhaustive switch, not a computed key:
 * every branch states its own null rule, and this is the ONLY function that
 * builds a body over the CLOSED `ValueKey | IdKey` union — so `ownerId`,
 * `projectId` and `accountLeadId` stay unsendable from here, not merely
 * unrendered.
 */
function dealPatch(key: ValueKey | IdKey, next: string): DealUpdateInput {
  switch (key) {
    // Not nullable server-side: an emptied name is refused by `validate` and
    // never reaches this function.
    case 'dealName':
      return { dealName: next };
    // The exact decimal string, never a number. `''` clears the column, and a
    // cleared amount is `null` — not `"0.00"`, which is a real price of zero.
    case 'amount':
      return { amount: next === '' ? null : next };
    case 'currency':
      return { currency: next === '' ? null : next.toUpperCase() };
    case 'closingDate':
      return { closingDate: next === '' ? null : next };
    case 'nextStep':
      return { nextStep: next === '' ? null : next };
    case 'stageId':
      return { stageId: next === '' ? null : Number(next) };
    case 'dealTypeId':
      return { dealTypeId: next === '' ? null : Number(next) };
    case 'leadSourceId':
      return { leadSourceId: next === '' ? null : Number(next) };
  }
}

/**
 * The messages a 422 carries that this field cannot mark, since it writes
 * exactly one key.
 *
 * Two sources, unioned: `routeFieldErrors([requestKey])` for anything named
 * against a different key, and the `.strict()` rejections, which name the
 * offending key in a sibling `key` field (`'key' in detail` is the
 * discriminator, read through a runtime guard since `detailPayload` is
 * `unknown`).
 *
 * A `Set` because `formMessages` already carries the unrecognised-key text;
 * the `key` probe keeps this correct if the wording moves again.
 */
function routeRefusal(
  caught: ApiError,
  requestKey: string,
): { onField: string | null; banner: string[] } {
  const { fields, unroutable } = caught.routeFieldErrors([requestKey]);
  const banner = new Set(unroutable);

  const raw = caught.detailPayload;
  if (Array.isArray(raw)) {
    for (const detail of raw) {
      if (detail === null || typeof detail !== 'object') continue;
      if (!('key' in detail)) continue;
      const message = (detail as { message?: unknown }).message;
      if (typeof message === 'string') banner.add(message);
    }
  }

  return { onField: fields[requestKey] ?? null, banner: [...banner] };
}

export interface InlineDealFieldsProps {
  deal: Deal;
  /**
   * Fires after every successful save, with the record the server returned.
   * The page decides what to do with it. **Nothing here pushes to Xero or
   * touches any integration** — a deal has no push.
   */
  onSaved: (saved: Deal) => void;
}

export function InlineDealFields({ deal, onSaved }: InlineDealFieldsProps) {
  const { ability } = useAuth();
  const canUpdate = ability.canUpdate('deals');

  const pickers = useDealPickers();

  /**
   * A refusal naming another key, or naming no key at all, has nowhere to go
   * in an inline editor — the field it was committed from isn't what the
   * server complained about. Shown above the fields instead, in the server's
   * own wording.
   */
  const [formNotice, setFormNotice] = useState<{ label: string; messages: string[] } | null>(null);

  /** Two quick saves can resolve out of order; only the newest may report. */
  const writeSeq = useRef(0);

  /*
   * A stage move onto an `isLost` stage (the flag, never the name) is not a
   * one-key PATCH: it opens `DealLostReasonDialog`, which writes `stageId` +
   * `lostReasonId` + `lostNote` together. The picker's save stays pending
   * until the dialog settles; cancelling writes nothing.
   */
  const [lostPrompt, setLostPrompt] = useState<{
    stage: { id: number; name: string };
    resolve: (result: InlineSaveResult) => void;
  } | null>(null);

  /**
   * Writes ONE column and reports the outcome in `InlineField`'s vocabulary.
   * Nothing here throws.
   *
   * `readBack` is passed only for ID-valued columns — a backstop rather than
   * load-bearing (deal write schemas ARE `.strict()`, so an unknown key 422s
   * rather than silently stripping). Kept because an id round-trips exactly
   * or not at all, unlike free text the server may normalise.
   *
   * `after` is the SECOND request some columns carry — today only Next task,
   * whose text is also a note on the deal and on its company. It runs once the
   * PATCH has landed, so everything it can report is about a save that already
   * succeeded: it may return a `partial`, never an `error`, and returning
   * `null` leaves the outcome a clean `saved`.
   *
   * ⚠️ **It cannot make the field's save fail, and that is the point.** The
   * column is written; reporting a refused follow-up as a failure would send
   * somebody to retype a value that is already stored, which writes the note
   * again and reports the same refusal.
   */
  const saveDealField = async (
    requestKey: string,
    label: string,
    patch: DealUpdateInput,
    readBack?: { sent: string; read: (saved: Deal) => string; noun: string },
    after?: (saved: Deal) => Promise<InlineSaveResult | null>,
  ): Promise<InlineSaveResult> => {
    const ticket = writeSeq.current + 1;
    writeSeq.current = ticket;
    setFormNotice(null);

    try {
      const response = await api.updateDeal(deal.id, patch);
      const saved = response.data;

      // Only the newest write may hand the record up: an older response would
      // reinstate the state it was taken from over a change made since.
      if (writeSeq.current === ticket) onSaved(saved);

      if (readBack && readBack.read(saved) !== readBack.sent) {
        return {
          status: 'warning',
          message: `The server accepted the request but the ${readBack.noun} on this deal is unchanged. Nothing was saved.`,
        };
      }

      /*
       * ⚠️ NOT inside the `writeSeq` guard. The PATCH landed, so its follow-up
       * belongs whether or not a newer write has overtaken this one — a note is
       * a history entry, and skipping it because somebody typed again would
       * quietly lose one. Only the REPORT is stale-guarded, and `InlineField`
       * already does that with its own ticket.
       */
      return (after ? await after(saved) : null) ?? { status: 'saved' };
    } catch (caught) {
      if (caught instanceof ApiError) {
        /*
         * A 403 carries no `details`, so it can never be a field error — and it
         * can arrive even though `canUpdate` said otherwise, because a role can
         * change under a live access token, which lasts over five days. A hidden
         * control is an optimisation; this branch is the guarantee.
         */
        if (caught.status === 403) {
          return {
            status: 'error',
            message: `${
              denialReason(ability, 'update', 'deals') ??
              'Your account no longer has permission to change this deal.'
            } Nothing was saved.`,
          };
        }
        if (caught.status === 404) {
          return {
            status: 'error',
            message: 'This deal no longer exists, so the change could not be saved.',
          };
        }

        // A 422 names the request key exactly, so it lands on the field that
        // caused it. Anything else is form-level and is banner-ed above.
        const { onField, banner } = routeRefusal(caught, requestKey);
        if (banner.length > 0) setFormNotice({ label, messages: banner });

        if (onField) return { status: 'error', message: onField };
        if (banner.length > 0) {
          return {
            status: 'error',
            message:
              'Refused — the reason is shown above. Nothing was saved.',
          };
        }
        return { status: 'error', message: caught.message };
      }
      return {
        status: 'error',
        message: 'Could not reach the server. Check your connection and try again.',
      };
    }
  };

  /**
   * The **Next task** follow-up: the same two notes `DealFormModal` writes, one
   * on the deal and one on the company that deal belongs to.
   *
   * Runs only after the `nextStep` PATCH has landed, so it can only ever return
   * a `partial` — the column IS saved.
   *
   * - **Only a real change writes anything.** `nextTaskNoteText` compares the
   *   typed text against `deal.nextStep`, the value loaded from the record,
   *   trimmed on both sides. `deal` here is the render's prop, so it is the
   *   pre-save value even though `onSaved` has already asked the page to
   *   re-read. Re-saving the same text records nothing, and CLEARING the field
   *   records nothing — an empty body is a 422, and the column already says
   *   there is no next task.
   * - **No company means ONE note**, and nothing is reported about a second: a
   *   note that was never attempted is not a note that failed.
   * - **A `/notes` 403 is reachable here** for somebody who may perfectly well
   *   edit this deal — it is one of the three modules the server enforces by
   *   permission set. It carries no `details`, so it is prose, not a field
   *   error, and the field still saved.
   *
   * ⚠️ The company is read from `deal.company` rather than from the response:
   * it is the company on screen, the one the reader believes this deal belongs
   * to, and a `nextStep` PATCH cannot move it. `saved.dealName` is the server's
   * own, so the company note names the deal as it is actually stored.
   */
  const recordNextTask = async (saved: Deal, next: string): Promise<InlineSaveResult | null> => {
    const text = nextTaskNoteText(next, deal.nextStep ?? '');
    if (text === '') return null;

    const failures = await writeNextTaskNotes(
      { dealId: saved.id, dealName: saved.dealName, companyId: deal.company?.id ?? null },
      text,
    );
    if (failures.length === 0) return null;
    return { status: 'partial', message: describeNextTaskNoteFailures(failures) };
  };

  /** The Stage picker's save: a lost stage goes through the dialog (see `lostPrompt`). */
  const saveStage = (next: string): Promise<InlineSaveResult> => {
    const target =
      next === '' ? undefined : pickers.stages.find((stage) => String(stage.id) === next);
    if (target?.isLost === true && target.id !== deal.stage?.id) {
      return new Promise<InlineSaveResult>((resolve) => {
        setFormNotice(null);
        setLostPrompt({ stage: { id: target.id, name: target.name }, resolve });
      });
    }
    return saveDealField('stageId', 'Stage', dealPatch('stageId', next), {
      sent: next,
      read: (saved) => readId(saved, 'stageId'),
      noun: 'stage',
    });
  };

  /*
   * Built as plain functions returning elements, never as components declared
   * in here: a component declared inside this one is a new type every
   * render, unmounting every field — losing its draft and "Saved" line — the
   * moment anything else on the page changes.
   */

  const textField = (
    key: ValueKey,
    label: string,
    extra: {
      display?: ReactNode;
      emptyLabel?: string;
      placeholder?: string;
      validate?: (next: string) => string | null;
      maxLength?: number;
      multiline?: boolean;
      rows?: number;
      type?: 'text' | 'date';
      inputMode?: 'text' | 'decimal' | 'numeric';
      /**
       * A second request this column carries, run once the PATCH has landed.
       * Destructured out rather than spread: `InlineField` takes no such prop
       * and its props are not an index signature, so a stray one is a `tsc`
       * error rather than a silently ignored attribute.
       */
      after?: (saved: Deal, next: string) => Promise<InlineSaveResult | null>;
    } = {},
  ) => {
    const { multiline, after, ...rest } = extra;
    return (
      <InlineField
        label={label}
        // The RAW stored string. A formatted one would make every blur a change.
        value={rawValue(deal, key)}
        kind={multiline ? 'multiline' : 'text'}
        disabled={!canUpdate}
        onSave={(next) =>
          saveDealField(
            key,
            label,
            dealPatch(key, next),
            undefined,
            after && ((saved) => after(saved, next)),
          )
        }
        {...rest}
      />
    );
  };

  /**
   * One of the three deal picklists, or the staff directory.
   *
   * - **The stored value is always in the list** — `selectablePickerOptions`
   *   keeps a retired-but-current option choosable, and the value is
   *   injected outright when the fetch didn't return it at all.
   * - **A failed fetch DISABLES the control** rather than showing an empty
   *   list beside a live "Unassigned" — the value still renders; only the
   *   affordance goes, with the reason said next to it.
   */
  const pickerField = (
    key: IdKey,
    label: string,
    stored: { id: number; name: string } | null,
    options: InlineFieldOption[],
    state: { loading: boolean; failed: boolean; truncated: boolean },
    display: ReactNode,
    emptyOptionLabel: string,
  ) => {
    /*
     * Why the control is inert, in the field's own status line — permission
     * is NOT said here; the one line above the fields covers that once.
     */
    const disabledReason =
      canUpdate && state.failed
        ? `The ${label.toLowerCase()} list couldn’t be loaded — that is a failed lookup, not an empty list. This can’t be changed until it loads; reload the page to try again.`
        : undefined;

    return (
      <InlineField
        kind="picker"
        label={label}
        value={stored ? String(stored.id) : ''}
        display={display}
        disabled={!canUpdate || state.failed}
        {...(disabledReason === undefined ? {} : { disabledReason })}
        options={options}
        optionsLoading={state.loading}
        emptyOptionLabel={emptyOptionLabel}
        placeholder={state.loading ? 'Loading…' : emptyOptionLabel}
        searchPlaceholder={`Search ${label.toLowerCase()}…`}
        onSave={(next) =>
          // Stage: a lost stage goes through the dialog — see `saveStage`.
          key === 'stageId'
            ? saveStage(next)
            : saveDealField(key, label, dealPatch(key, next), {
                sent: next,
                read: (saved) => readId(saved, key),
                noun: label.toLowerCase(),
              })
        }
      />
    );
  };

  const stageOptions = pickerOptions(
    selectablePickerOptions(pickers.stages, deal.stage?.id ?? null),
    deal.stage,
  );
  const typeOptions = pickerOptions(
    selectablePickerOptions(pickers.dealTypes, deal.dealType?.id ?? null),
    deal.dealType,
  );
  const sourceOptions = pickerOptions(
    selectablePickerOptions(pickers.leadSources, deal.leadSource?.id ?? null),
    deal.leadSource,
  );

  return (
    <div className="grid min-w-0 gap-4">
      {formNotice && (
        /* Form level: the server named something this field doesn't write. */
        <Notice tone="danger" role="alert" title={<>Saving {formNotice.label} was refused.</>}>
          {formNotice.messages.map((message) => (
            <p key={message} className="m-0 mt-1 [overflow-wrap:anywhere] text-fg-secondary">
              {message}
            </p>
          ))}
          <p className="m-0 mt-1 text-fg-muted">Nothing was saved.</p>
        </Notice>
      )}

      {!canUpdate && (
        <p className="m-0 text-xs text-fg-muted">
          {denialReason(ability, 'update', 'deals') ?? 'You cannot change this deal.'}
        </p>
      )}

      <Group title="Name">
        <Item label="Deal name" wide>
          {textField('dealName', 'Deal name', {
            maxLength: DEAL_MAX_LENGTHS.dealName,
            validate: validateDealName,
          })}
        </Item>
      </Group>

      <Group title="Value">
        <Item label="Amount">
          {textField('amount', 'Amount', {
            validate: validateDealAmount,
            // `decimal`, never `numeric`: a numeric keypad has no decimal point,
            // and money here is a decimal string.
            inputMode: 'decimal',
            emptyLabel: 'Not priced',
            /*
             * `formatDecimalString` renders the stored string with
             * separators; it never parses it. No aggregate anywhere — one
             * total across mixed currencies is wrong by construction.
             */
            /*
             * 🔴 **THE FIGURE SHOWN IS `dealValue`, THE EDITOR STILL WRITES
             * `amount` — owner's ruling, 10 September 2026.** A deal's value is
             * its LINE TOTAL whenever it has lines, and the typed `amount` only
             * when it has none.
             *
             * ⚠️ **So this cell can show a figure the input below it does not
             * contain, and that is the one thing it must say out loud.** Opening
             * the editor on Michigan — 37,500.00 of line items, empty Amount box
             * — would otherwise present a blank field over a real number and
             * read as the value having been lost. `dealValueIsFromLines` is true
             * in exactly that case, and only then.
             *
             * ⚠️ **Not a second row.** A "Deal value" item beside this one would
             * put two money figures in disagreement one above the other, which
             * is the confusion the ruling exists to remove. One value, one
             * control, one caption.
             */
            display:
              dealValue(deal) === null ? (
                <span className="text-fg-subtle">Not priced</span>
              ) : (
                <span className="grid gap-0.5">
                  <span className="tabular">
                    {formatDecimalString(dealValue(deal) as string)}
                    {deal.currency && <span className="ml-1 text-fg-muted">{deal.currency}</span>}
                  </span>
                  {dealValueIsFromLines(deal) && (
                    <span className="text-xs text-fg-muted">
                      From the products below. The Amount field is empty.
                    </span>
                  )}
                </span>
              ),
          })}
        </Item>
        <Item label="Currency">
          {textField('currency', 'Currency', {
            maxLength: 3,
            validate: validateDealCurrency,
            emptyLabel: 'Not set',
          })}
        </Item>
      </Group>

      <Group title="Pipeline">
        <Item label="Stage">
          {pickerField(
            'stageId',
            'Stage',
            deal.stage,
            stageOptions,
            {
              loading: pickers.loading,
              failed: pickers.failedBy.stages,
              truncated: pickers.truncatedBy.stages,
            },
            <DealStageBadge stage={deal.stage} />,
            'No stage',
          )}
        </Item>
        <Item label="Deal type">
          {pickerField(
            'dealTypeId',
            'Deal type',
            deal.dealType,
            typeOptions,
            {
              loading: pickers.loading,
              failed: pickers.failedBy.dealTypes,
              truncated: pickers.truncatedBy.dealTypes,
            },
            <LookupValue value={deal.dealType} />,
            'Not set',
          )}
        </Item>
        <Item label="Lead source">
          {pickerField(
            'leadSourceId',
            'Lead source',
            deal.leadSource,
            sourceOptions,
            {
              loading: pickers.loading,
              failed: pickers.failedBy.leadSources,
              truncated: pickers.truncatedBy.leadSources,
            },
            <LookupValue value={deal.leadSource} />,
            'Not set',
          )}
        </Item>
        <Item label="Closing date">
          {textField('closingDate', 'Closing date', {
            /*
             * A native date control's `value` is `YYYY-MM-DD` — the exact
             * stored string, read back unchanged. `validateDealClosingDate`
             * still runs; it's what refuses `2026-02-30`.
             */
            type: 'date',
            validate: validateDealClosingDate,
            emptyLabel: 'No date',
            /* `formatDateOnly` reads 'YYYY-MM-DD' as calendar parts — never
               `new Date(closingDate)`, which is midnight UTC. */
            display:
              deal.closingDate === null ? (
                <span className="text-fg-subtle">No date</span>
              ) : (
                formatDateOnly(deal.closingDate)
              ),
          })}
        </Item>
        {/*
          ⚠️ The label was renamed from "Next step" on the owner's instruction
          (2026-08-31). The key stays `nextStep`: it is the wire key, the
          `ValueKey` member and the `d_deals` column, and `dealPatch` builds
          `{ nextStep }` from it.

          ⚠️ **THE ONLY FIELD HERE WITH A SECOND REQUEST BEHIND IT.** Saving it
          also writes the two notes `DealFormModal` writes. That was left out
          when the rename shipped, because `InlineField.onSave` had three
          TERMINAL outcomes and "the column saved, its note was refused" could
          not be reported; the `partial` outcome exists to say exactly that, so
          the gap is closed rather than worked around.

          ⚠️ **NOT gated on `notes` permission**, deliberately: gating would hide
          a `d_deals` column from somebody allowed to write it, on the strength
          of an unrelated module. The 403 is handled where it arrives.
        */}
        <Item label="Next task">
          {textField('nextStep', 'Next task', {
            maxLength: DEAL_MAX_LENGTHS.nextStep,
            emptyLabel: 'Not set',
            after: recordNextTask,
          })}
        </Item>
        {/*
          Why the deal was lost (migration 286), read-only and only when set.
          Written by `DealLostReasonDialog` on a move to an `isLost` stage.
          `undefined` is an older API and shows nothing.
        */}
        {deal.lostReason && (
          <Item label="Lost reason">
            <span className="min-w-0">{deal.lostReason.name}</span>
          </Item>
        )}
        {deal.lostReason && deal.lostNote && (
          <Item label="Lost note" wide>
            <span className="min-w-0 whitespace-pre-line">{deal.lostNote}</span>
          </Item>
        )}
        {/*
          ⚠️ THE OWNER PICKER STOOD HERE; THE SLOT IS DELIBERATELY EMPTY.
          Owner is gone from deals; `ownerId` is not a member of `IdKey`, so
          nothing here can write it. What replaced it — the ACCOUNT LEAD, a
          DIFFERENT person — is rendered by `DealDetailPage`, in its
          Relationship group below, fully EDITABLE. Not repeated here: a
          read-only copy would be a second row for one value, and it would
          look inert next to a working picker three rows down. The page can
          also see `companyAccountLead`, which lets its empty option say
          "Follow the company (…)" rather than "None" — this component
          cannot.

          If mounted somewhere WITHOUT that row, add the account lead back
          as plain text — never a disabled control, never writing
          `accountLeadId`.
        */}
      </Group>

      {/*
        ⚠️ A "Description" group stood here, editing `deal.description` in
        place. Removed on the owner's decision, 2026-08-31, together with the
        Description control on `DealFormModal` and the read-only Quote ID on
        `DealDetailPage`. See `TextKey` for why the KEY had to go with the
        control — and for the fact that the column still holds real data and
        is still returned on `Deal`; this hides it, it deletes nothing.
      */}

      {lostPrompt && (
        <DealLostReasonDialog
          open
          deal={{ id: deal.id, dealName: deal.dealName }}
          stage={lostPrompt.stage}
          onCancel={() => {
            lostPrompt.resolve({ status: 'warning', message: 'Stage not changed.' });
            setLostPrompt(null);
          }}
          onSaved={(saved) => {
            writeSeq.current += 1;
            onSaved(saved);
            lostPrompt.resolve({ status: 'saved' });
            setLostPrompt(null);
          }}
        />
      )}
    </div>
  );
}

/**
 * The stored string for one column, exactly as held. `InlineField` compares
 * this against the draft to decide whether anything changed, so it must
 * never be formatted — comparing against `18,000.00 GBP` would make every
 * blur a change.
 */
function rawValue(deal: Deal, key: ValueKey): string {
  switch (key) {
    case 'dealName':
      return deal.dealName;
    case 'amount':
      return deal.amount ?? '';
    case 'currency':
      return deal.currency ?? '';
    case 'closingDate':
      return deal.closingDate ?? '';
    case 'nextStep':
      return deal.nextStep ?? '';
  }
}

/** The same id read back off the saved record, for the applied-check. */
function readId(saved: Deal, key: IdKey): string {
  const held =
    key === 'stageId' ? saved.stage : key === 'dealTypeId' ? saved.dealType : saved.leadSource;
  return held ? String(held.id) : '';
}

/**
 * A picklist's options, with the record's own value guaranteed present.
 * `selectablePickerOptions` already applies `isActive || id === current`; this
 * adds the value the fetch never returned at all (failed, or past the
 * 100-row page). A picker that silently drops the record's own value reads
 * as "unassigned" and reassigns it on the next save.
 */
function pickerOptions(
  rows: readonly { id: number; name: string; isActive: boolean }[],
  stored: LookupSummary | StageSummary | null,
): InlineFieldOption[] {
  const options: InlineFieldOption[] = rows.map((row) => ({
    value: String(row.id),
    label: row.name,
    ...(row.isActive ? {} : { hint: 'Retired — no longer offered' }),
  }));
  if (stored && !options.some((option) => option.value === String(stored.id))) {
    options.unshift({ value: String(stored.id), label: stored.name, hint: 'Currently set' });
  }
  return options;
}

function LookupValue({ value }: { value: LookupSummary | null }) {
  if (!value) return <span className="text-fg-subtle">Not set</span>;
  return <span className="min-w-0">{value.name}</span>;
}

/**
 * No account-lead renderer here — that row lives on `DealDetailPage` (see
 * the Pipeline group comment). Whatever renders it must never print `''`:
 * `UserSummary.name` is typed `string` and can be empty, so fall back to
 * `name || email || Staff #id`.
 */

/**
 * One labelled row. The same shape as the detail page's own `DetailItem`, which
 * is not exported — repeated rather than imported so this component stays
 * self-contained and nothing here depends on another agent's file.
 */
function Item({ label, wide, children }: { label: string; wide?: boolean; children: ReactNode }) {
  return (
    <div className={wide ? 'min-w-0 sm:col-span-2 lg:col-span-3' : 'min-w-0'}>
      <dt className="text-xs text-fg-muted">{label}</dt>
      {/* `min-w-0` throughout: a grid item defaults to min-width:auto, which is
          what pushes a page sideways at 320px rather than wrapping. */}
      <dd className="mt-0.5 min-w-0 text-sm text-fg-secondary [overflow-wrap:anywhere]">
        {children}
      </dd>
    </div>
  );
}

function Group({
  title,
  columns = 'sm:grid-cols-2 lg:grid-cols-3',
  children,
}: {
  title: string;
  columns?: string;
  children: ReactNode;
}) {
  return (
    <section className="min-w-0">
      <h3 className="mb-2 text-sm font-semibold text-fg">{title}</h3>
      {/* One column below `sm`, always. */}
      <dl className={`grid min-w-0 gap-x-4 gap-y-3 ${columns}`}>{children}</dl>
    </section>
  );
}
